import React, { useState } from "react";
import HourSelection, { type HourSelectionProps } from "./HourSelection.js";
import { SelectInput } from "../../../controls";
import type { SelectOption } from "../../../controls/SelectInput/SelectInput";
import { SCHEDULE_TYPES, type SchedulerRunnerDataData } from "../SchedulerUtils";
import SchedulerTimes from "./SchedulerTimes.js";
import WeekdaySelection from "./WeekdaySelection.js";

interface WeeklyIntervalOption {
  value: number;
  label: number;
}

// At runtime the interval labels are numbers (1-4); SelectInput renders them
// as-is. SelectOption types `label` as string, so the options are cast to
// SelectOption at the boundary rather than widening the shared SelectOption.
const weeklyIntervalOptions: WeeklyIntervalOption[] = [1, 2, 3, 4].map(
  (val) => ({
    value: val,
    label: val,
  })
);

const weeklyIntervalSelectOptions =
  weeklyIntervalOptions as unknown as SelectOption[];

export interface WeeklyRunnerProps {
  disabled?: boolean;
  HourSelectionComponent?: React.ComponentType<HourSelectionProps>;
  data?: SchedulerRunnerDataData;
  onChange: (data: {
    type: typeof SCHEDULE_TYPES.WEEK;
    data: SchedulerRunnerDataData;
  }) => void;
  disableInterval?: boolean;
  hideInterval?: boolean;
}

const WeeklyRunner: React.FC<WeeklyRunnerProps> = ({
  disabled = false,
  HourSelectionComponent = HourSelection,
  data,
  onChange,
  disableInterval = false,
  hideInterval = false,
}) => {
  const [intervalCount, setIntervalCount] = useState<number | undefined>(
    disableInterval || hideInterval ? 1 : data?.interval
  );
  const [times, setTimes] = useState<string[]>(data?.times || ["00:00"]);
  const [selectedDays, setSelectedDays] = useState<Array<string | number>>(
    data?.selectedDays || []
  );
  const onTimesChange = (times: string[]) => {
    setTimes(times);
    onChange({
      type: SCHEDULE_TYPES.WEEK,
      data: {
        interval: intervalCount,
        times,
        selectedDays,
      },
    });
  };

  const onIntervalChange = ({ value }: SelectOption) => {
    setIntervalCount(parseInt(value));
    onChange({
      type: SCHEDULE_TYPES.WEEK,
      data: {
        interval: parseInt(value),
        times,
        selectedDays,
      },
    });
  };

  const onWeekdayChange = (selectedDays: Array<string | number>) => {
    setSelectedDays(selectedDays);
    onChange({
      type: SCHEDULE_TYPES.WEEK,
      data: {
        interval: intervalCount,
        times,
        selectedDays,
      },
    });
  };

  return (
    <div
      className="weekly-runner-container"
      data-testid="weekly-runner-container"
    >
      {!hideInterval && (
        <div className="interval-container">
          <SelectInput
            label="Every"
            required={true}
            placeholder={"#"}
            size="xxs"
            name="weekly-runner-count"
            data-testid="weekly-runner-count"
            value={weeklyIntervalSelectOptions.find(
              (option) => option.value === intervalCount
            )}
            options={weeklyIntervalSelectOptions}
            onChange={onIntervalChange}
            disabled={disableInterval}
          />
          <span
            className={`scheduler-week-input-label-right ${disableInterval ? "is-disabled" : ""}`}
          >
            {intervalCount ? (intervalCount === 1 ? "week" : "weeks") : ""}
          </span>
        </div>
      )}
      <WeekdaySelection
        onChange={onWeekdayChange}
        // The WEEK runner only ever holds day-name strings here (MONTH uses
        // numbers); the shared state type is the wider string|number union.
        selectedDays={selectedDays as string[]}
        multi={true}
      />
      <div className="times-container">
        <SchedulerTimes
          maxTimeSlots={1}
          initialTimes={times}
          onChange={onTimesChange}
          HourSelectionComponent={HourSelectionComponent}
          label="Select Time(s)"
          data-testid={"scheduler-weekly"}
          disabled={disabled}
        />
      </div>
    </div>
  );
};

export default WeeklyRunner;
